/**
 * SkillImpact — "¿qué skills valen la pena entrenar?".
 *
 *  - Summary: 30d mining value (Jita buy) + reprocess baseline.
 *  - Por skill crítica: current level, Δ ISK/mes del próximo nivel,
 *    cumulativo a MAX, reasoning del cálculo.
 *  - Toggle 30d / current_month.
 */

import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { api, formatISKShort } from '../api'
import { LastUpdated } from '../components/LastUpdated'
import { RefreshButton } from '../components/RefreshButton'
import { LoadingSkeleton } from '../components/LoadingSkeleton'
import { useForceRefetch } from '../hooks/useForceRefetch'

type SkillImpactEntry = {
  skill_id: number
  name: string
  kind: 'reprocessing' | 'mining_yield'
  current_level: number
  max_level: number
  next_level: number | null
  delta_per_level_pct: number
  next_level_delta_isk_per_month: number | null
  cumulative_delta_isk_to_max: number | null
  remaining_levels: number
  trained: boolean
  reasoning: string
}

export function SkillImpact() {
  const [period, setPeriod] = useState<'30d' | 'current_month'>('current_month')
  const { key: forceRefetchKey, force, bump } = useForceRefetch()

  const currentMonth = period === 'current_month'
  const days = period === '30d' ? 30 : undefined

  const { data, isLoading, dataUpdatedAt, isFetching } = useQuery({
    queryKey: ['skill-impact', forceRefetchKey, period],
    queryFn: () => api.skillImpact({ days, currentMonth }, force),
  })

  const skills: SkillImpactEntry[] = data?.skills ?? []
  const miningValue = data?.mining_value_jita_buy_30d ?? 0
  const reprocessValue = data?.reprocess_value_jita_buy_30d ?? 0
  const observed = data?.observed_days ?? 0

  const topSkill = skills.find((s) => !s.trained && s.next_level_delta_isk_per_month !== null)

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold">Skill impact</h1>
          <p className="text-eve-muted text-sm">
            Para tus 4 skills críticas de industrialist, cuánto ISK/mes rinde
            el próximo nivel — data-driven, sin input.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <PeriodToggle value={period} onChange={setPeriod} />
          <LastUpdated timestamp={dataUpdatedAt} label="" />
          <RefreshButton onClick={bump} isFetching={isFetching} />
        </div>
      </div>

      {/* Summary tiles */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <SummaryTile
          label="Mining value (30d)"
          value={`${formatISKShort(miningValue)} ISK`}
          sub={`a Jita buy · observado ${observed}d`}
        />
        <SummaryTile
          label="Reprocess value (30d)"
          value={`${formatISKShort(reprocessValue)} ISK`}
          sub="ore reprocesado a Jita buy de minerales"
          accent={reprocessValue > 0}
        />
        <SummaryTile
          label="Top skill"
          value={
            topSkill
              ? `${topSkill.name}`
              : miningValue === 0 && reprocessValue === 0
                ? 'sin data'
                : 'maxed'
          }
          sub={
            topSkill?.next_level_delta_isk_per_month != null
              ? `+${formatISKShort(topSkill.next_level_delta_isk_per_month)} ISK/mes`
              : '—'
          }
          positive={!!topSkill}
        />
      </div>

      {/* Skill cards */}
      <div className="card">
        {isLoading ? (
          <LoadingSkeleton variant="rows" rows={6} />
        ) : skills.length === 0 ? (
          <div className="text-eve-muted py-8 text-center italic">
            Sin skills para analizar.
          </div>
        ) : (
          <div className="space-y-3">
            {skills.map((s) => (
              <SkillRow key={s.skill_id} skill={s} />
            ))}
          </div>
        )}

        {miningValue === 0 && reprocessValue === 0 && !isLoading && (
          <div className="text-eve-warn text-xs italic mt-3">
            Sin actividad de mining en este período. Para entrenar y empezar a
            tener data, salí a minar algo — después volvé acá.
          </div>
        )}
      </div>

      <p className="text-xs text-eve-muted">
        Hipótesis conservadoras: reprocess asume yield base 50% + 2%/3% por
        nivel + aditivo entre Reprocessing y Reprocessing Efficiency (sin
        implant, sin station bonus). Mining skills asumen +5%/nivel uniforme
        sobre el Jita buy value del ore. SP/hora del char no entran al cálculo
        (cuánto tarda en entrenar depende del char).
      </p>
    </div>
  )
}

function PeriodToggle({
  value,
  onChange,
}: {
  value: '30d' | 'current_month'
  onChange: (p: '30d' | 'current_month') => void
}) {
  return (
    <div className="flex gap-1">
      {(
        [
          { key: 'current_month' as const, label: 'Month' },
          { key: '30d' as const, label: '30d' },
        ]
      ).map((o) => (
        <button
          key={o.key}
          onClick={() => onChange(o.key)}
          className={`text-[11px] uppercase tracking-wider px-2 py-1 rounded border transition-colors ${
            value === o.key
              ? 'bg-eve-accent text-eve-bg border-eve-accent font-semibold'
              : 'bg-eve-panel border-eve-border text-eve-muted hover:text-eve-text'
          }`}
        >
          {o.label}
        </button>
      ))}
    </div>
  )
}

function SummaryTile({
  label,
  value,
  sub,
  accent,
  positive,
}: {
  label: string
  value: string
  sub?: string
  accent?: boolean
  positive?: boolean
}) {
  const cls = positive
    ? 'text-eve-success'
    : accent
      ? 'text-eve-accent'
      : 'text-eve-text'
  return (
    <div className="card">
      <div className="stat-label">{label}</div>
      <div className={`text-lg font-semibold mt-0.5 ${cls}`}>{value}</div>
      {sub && <div className="text-xs text-eve-muted mt-1 italic">{sub}</div>}
    </div>
  )
}

function SkillRow({ skill }: { skill: SkillImpactEntry }) {
  const { trained, current_level, max_level, remaining_levels } = skill
  const nextDelta = skill.next_level_delta_isk_per_month
  const cumDelta = skill.cumulative_delta_isk_to_max

  return (
    <div
      className={`border rounded-md p-3 ${
        trained
          ? 'border-eve-border/30 bg-eve-bg/20 opacity-70'
          : 'border-eve-border bg-eve-bg/40'
      }`}
    >
      <div className="flex items-baseline justify-between flex-wrap gap-2">
        <div className="flex items-baseline gap-2 flex-wrap">
          <span className="font-medium">{skill.name}</span>
          <span className="text-xs text-eve-muted">
            L{current_level} / L{max_level}
          </span>
          <span className="text-[10px] text-eve-muted font-mono">
            +{skill.delta_per_level_pct}% / nivel
          </span>
          {trained ? (
            <span className="text-[10px] uppercase font-mono px-1.5 py-0.5 rounded bg-eve-success/10 border border-eve-success text-eve-success">
              MAXED
            </span>
          ) : remaining_levels > 0 ? (
            <span className="text-[10px] uppercase font-mono px-1.5 py-0.5 rounded bg-eve-panel border border-eve-border text-eve-muted">
              {remaining_levels} lvl restantes
            </span>
          ) : null}
        </div>

        <div className="text-right">
          {trained ? (
            <div className="text-eve-muted text-sm italic">Maxed</div>
          ) : nextDelta !== null && skill.next_level !== null ? (
            <div>
              <div className="stat-label">
                L{current_level} → L{skill.next_level}
              </div>
              <div className="font-mono text-base font-semibold text-eve-success">
                +{formatISKShort(nextDelta)} ISK / mes
              </div>
            </div>
          ) : (
            <div className="text-eve-muted text-xs italic">sin data</div>
          )}
        </div>
      </div>

      {/* Leveled bar */}
      <div className="flex items-center gap-1 mt-2">
        {Array.from({ length: 5 }).map((_, i) => (
          <div
            key={i}
            className={`flex-1 h-1.5 rounded ${
              i < current_level ? 'bg-eve-accent' : 'bg-eve-border/40'
            }`}
          />
        ))}
      </div>

      {/* Cumulative to MAX */}
      {!trained && cumDelta !== null && remaining_levels > 1 && (
        <div className="text-xs text-eve-muted mt-2 italic">
          Si entrenás del {current_level} al {max_level}:{' '}
          <span className="font-mono text-eve-accent">
            +{formatISKShort(cumDelta)} ISK / mes cumulativo
          </span>
        </div>
      )}

      <p className="text-xs text-eve-muted mt-2">{skill.reasoning}</p>
    </div>
  )
}
